<template>
  <div
    v-if="showWidget"
    class="fixed bottom-6 right-6 z-10 shadow-xl rounded-2xl transition-all duration-300 hover:shadow-2xl hover:scale-105 max-w-sm border-4 overflow-hidden"
    :class="mainBorderClass"
    style="width: 340px;"
  >
    <!-- Congratulations Banner (when milestone reached) -->
    <div
      v-if="showCongratulations"
      class="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-5 py-3 flex items-center animate-pulse"
    >
      <svg class="w-6 h-6 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
      </svg>
      <div>
        <div class="font-bold text-base">Congratulations!</div>
        <div class="text-sm opacity-95">{{ currentDiscount }}% discount unlocked</div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="px-5 py-3" :class="backgroundClass">
        <!-- Title with collapse button -->
        <div class="flex items-center justify-between mb-2">
          <div class="font-bold text-sm" :class="textClasses">
            {{ isExpired ? 'Trial Expired' : `Trial: ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} left` }}
          </div>
          <div class="flex items-center gap-2">
            <router-link 
              v-if="!isExpired"
              to="/task-list"
              class="text-xs font-semibold px-2 py-1 rounded hover:bg-black hover:bg-opacity-5 transition-colors bg-transparent"
              :class="textClasses"
            >
              View Tasks
            </router-link>
              <div class="relative mr-2">
                <button
                  engagekit-task-list-launcher
                  engagekit-task-list-display-type="badge">
                </button>
              </div>
            <button 
              @click="toggleCollapse"
              class="p-1 rounded hover:bg-black hover:bg-opacity-5 transition-colors"
              :class="textClasses"
            >
              <svg 
                class="w-4 h-4 transition-transform duration-200" 
                :class="{ 'rotate-180': isCollapsed }"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
        
        <!-- Trial Status Header - Only show when expanded -->
        <div v-if="!isCollapsed" class="mb-3">
          <div v-if="isExpired">
            <router-link to="/subscription" class="block" :class="textClasses">
              <div class="text-xs underline opacity-75 hover:opacity-100 transition-opacity">
                Subscribe now
              </div>
            </router-link>
          </div>
          <div v-else>
            <!-- Task completion message -->
            <router-link 
              v-if="credits < 1000"
              to="/task-list" 
              class="block text-xs font-semibold hover:opacity-100 transition-opacity no-underline bg-transparent mb-3"
              :class="textClasses"
            >
              Complete setup tasks to unlock your best subscription price. <span class="underline">View tasks →</span>
            </router-link>
            
            <!-- Subscribe button with dynamic discount -->
            <div>
              <div v-if="credits >= 1000" class="text-xs font-medium mb-2" :class="textClasses">
                Amazing! You've unlocked 50% off 🎉
              </div>
              <router-link 
                to="/subscription"
                class="inline-block px-4 py-2 text-xs font-bold rounded-lg shadow-sm transition-all duration-200 border-yellow-600 border-2 bg-yellow-200 text-yellow-600 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-50"
                style="box-shadow: inset 0px -6px 0px #FBBF24, inset 0 -7px 0 0 #FFFBEB, inset 0px 1px 0px 1px #FFFBEB;"
              >
                {{ subscribeButtonText }}
              </router-link>
            </div>
          </div>
        </div>

        <!-- Credits Section (only when expanded) -->
        <div v-if="!isCollapsed" class="space-y-2">
          <!-- Credits Count and Progress Message on Same Line -->
          <div class="flex items-center justify-between text-xs font-semibold" :class="textClasses">
            <span>
              <span class="text-base font-bold">{{ credits }}</span><span class="opacity-60"> / 1300</span>
            </span>
            <span v-if="nextMilestone">
              <span class="font-bold">{{ creditsToNextMilestone }}</span> credits to unlock <span class="font-bold">{{ nextDiscount }}% off</span>
            </span>
            <span v-else>
              🎉 Max unlocked!
            </span>
          </div>

          <!-- Progress Bar with Enhanced Style -->
          <div class="relative py-4">
            <div class="relative w-full h-2.5 bg-white rounded-full overflow-hidden shadow-inner border-2 border-gray-200">
              <div
                class="absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out shadow-sm"
                :class="progressBarClass"
                :style="{ width: progressPercentage + '%' }"
              >
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
              </div>
            </div>

            <!-- Milestones positioned on the progress bar -->
            <div 
              v-for="milestone in milestones" 
              :key="milestone.credits"
              class="absolute flex flex-col items-center transition-all duration-300"
              :class="credits >= milestone.credits ? 'scale-110' : ''"
              :style="{ left: `calc(${(milestone.credits / 1300) * 100}% - 16px)`, top: '4px' }"
            >
              <div 
                class="flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-300 relative"
                :class="credits >= milestone.credits 
                  ? 'bg-gradient-to-br from-yellow-400 to-amber-500 border-yellow-600 shadow-lg' 
                  : 'bg-white border-gray-300'"
              >
                <span 
                  v-if="credits >= milestone.credits"
                  class="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center shadow-sm"
                >
                  <svg class="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </span>
                <span 
                  class="font-bold transition-all"
                  style="font-size: 9px;"
                  :class="credits >= milestone.credits ? 'text-white' : 'text-gray-400'"
                >
                  {{ milestone.discount }}%
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Progress bar always visible (when collapsed) -->
        <div v-else class="relative py-4 mt-2">
            <div class="relative w-full h-2.5 bg-white rounded-full overflow-hidden shadow-inner border-2 border-gray-200">
              <div
                class="absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out shadow-sm"
                :class="progressBarClass"
                :style="{ width: progressPercentage + '%' }"
              >
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
              </div>
            </div>

            <!-- Milestones positioned on the progress bar -->
            <div 
              v-for="milestone in milestones" 
              :key="milestone.credits"
              class="absolute flex flex-col items-center transition-all duration-300"
              :class="credits >= milestone.credits ? 'scale-110' : ''"
              :style="{ left: `calc(${(milestone.credits / 1300) * 100}% - 16px)`, top: '4px' }"
            >
              <div 
                class="flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-300 relative"
                :class="credits >= milestone.credits 
                  ? 'bg-gradient-to-br from-yellow-400 to-amber-500 border-yellow-600 shadow-lg' 
                  : 'bg-white border-gray-300'"
              >
                <span 
                  v-if="credits >= milestone.credits"
                  class="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center shadow-sm"
                >
                  <svg class="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </span>
                <span 
                  class="font-bold transition-all"
                  style="font-size: 9px;"
                  :class="credits >= milestone.credits ? 'text-white' : 'text-gray-400'"
                >
                  {{ milestone.discount }}%
                </span>
              </div>
            </div>
        </div>
      </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'TrialWidget',
  data() {
    return {
      showCongratulations: false,
      isCollapsed: false,
      milestones: [
        { credits: 500, discount: 10 },
        { credits: 750, discount: 20 },
        { credits: 1000, discount: 50 }
      ]
    };
  },
  computed: {
    ...mapGetters("auth", ["currentUser"]),
    
    showWidget() {
      if (!this.currentUser) return false;
      
      // Only show for trial users (trialing or trial_expired)
      const isTrial = this.currentUser.status === 'trialing' || this.currentUser.status === 'trial_expired';
      if (!isTrial) return false;
      
      // Check if trial started on or after January 29, 2026 10:00 AM UTC
      // Unix timestamp: 1738148400 (2026-01-29 10:00:00 UTC)
      const cutoffTimestamp = 1769299200; // January 25, 2026 00:00:00 AM
      const trialStart = this.currentUser.app_metadata?.trial_start;
      
      if (!trialStart) return false;
      
      return trialStart >= cutoffTimestamp;
    },
    
    credits() {
      // Get credits from user metadata, default to 0
      return this.currentUser?.app_metadata?.trial_credits || 0;
    },
    
    currentMilestone() {
      // Find the highest milestone reached
      return [...this.milestones]
        .reverse()
        .find(m => this.credits >= m.credits);
    },
    
    currentDiscount() {
      return this.currentMilestone?.discount || 0;
    },
    
    nextMilestone() {
      // Find the next milestone to reach
      return this.milestones.find(m => this.credits < m.credits);
    },
    
    nextDiscount() {
      return this.nextMilestone?.discount || 0;
    },
    
    creditsToNextMilestone() {
      if (!this.nextMilestone) return 0;
      return this.nextMilestone.credits - this.credits;
    },
    
    progressPercentage() {
      const maxCredits = 1300;
      return Math.min((this.credits / maxCredits) * 100, 100);
    },
    
    isExpired() {
      return this.currentUser && this.currentUser.status === 'trial_expired';
    },
    
    trialEndText() {
      if (this.currentUser && this.currentUser.trial_end) {
        return this.currentUser.trial_end;
      }
      return 'Trial active';
    },
    
    daysRemaining() {
      // Extract days from trial_end string like "Trial: 15 days left"
      if (this.currentUser && this.currentUser.trial_end) {
        const match = this.currentUser.trial_end.match(/(\d+)\s*days?/);
        if (match) return parseInt(match[1]);
      }
      return 0;
    },
    
    mainBorderClass() {
      if (this.isExpired) {
        return 'border-red-900';
      }
      return 'border-yellow-600';
    },
    
    backgroundClass() {
      if (this.isExpired) {
        return 'bg-red-50';
      }
      return 'bg-yellow-50';
    },
    
    textClasses() {
      if (this.isExpired) {
        return 'text-red-800';
      }
      return 'text-yellow-800';
    },
    
    iconBgClass() {
      if (this.isExpired) {
        return 'bg-red-100';
      }
      return 'bg-yellow-100';
    },
    
    iconColorClass() {
      if (this.isExpired) {
        return 'text-red-700';
      }
      return 'text-yellow-700';
    },
    
    borderClass() {
      if (this.isExpired) {
        return 'border-red-200';
      }
      return 'border-yellow-200';
    },
    
    progressBarClass() {
      if (this.credits >= 1000) {
        return 'bg-gradient-to-r from-yellow-400 to-amber-500';
      } else if (this.credits >= 750) {
        return 'bg-gradient-to-r from-yellow-400 to-yellow-500';
      } else if (this.credits >= 500) {
        return 'bg-gradient-to-r from-yellow-300 to-yellow-500';
      }
      return 'bg-gradient-to-r from-yellow-300 to-amber-400';
    },
    
    subscribeButtonText() {
      if (this.credits >= 1000) {
        return 'Subscribe with 50% off →';
      } else if (this.credits >= 750) {
        return 'Subscribe with 20% off →';
      } else if (this.credits >= 500) {
        return 'Subscribe with 10% off →';
      }
      return 'Subscribe →';
    }
  },
  watch: {
    credits(newVal, oldVal) {
      // Check if a milestone was just reached
      if (oldVal && newVal > oldVal) {
        const justReached = this.milestones.find(
          m => oldVal < m.credits && newVal >= m.credits
        );
        
        if (justReached) {
          this.triggerCongratulations();
        }
      }
    }
  },
  mounted() {
    this.loadCollapsedState();
  },
  methods: {
    triggerCongratulations() {
      this.showCongratulations = true;
      setTimeout(() => {
        this.showCongratulations = false;
      }, 5000); // Hide after 5 seconds
    },
    addCredits(amount) {
      // Update credits in the store by modifying currentUser
      if (this.currentUser && this.currentUser.app_metadata) {
        const currentCredits = this.currentUser.app_metadata.trial_credits || 0;
        const newCredits = Math.min(currentCredits + amount, 1300);
        
        // Update the user object in the store
        this.$store.commit('auth/SET_CURRENT_USER', {
          ...this.currentUser,
          app_metadata: {
            ...this.currentUser.app_metadata,
            trial_credits: newCredits
          }
        });
      }
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      this.saveCollapsedState();
    },
    saveCollapsedState() {
      const today = new Date().toDateString();
      localStorage.setItem('trialWidgetCollapsed', JSON.stringify({
        collapsed: this.isCollapsed,
        date: today
      }));
    },
    loadCollapsedState() {
      try {
        const stored = localStorage.getItem('trialWidgetCollapsed');
        if (stored) {
          const { collapsed, date } = JSON.parse(stored);
          const today = new Date().toDateString();
          
          // Only restore collapsed state if it's from today
          if (date === today) {
            this.isCollapsed = collapsed;
          } else {
            // Clear old state
            localStorage.removeItem('trialWidgetCollapsed');
          }
        }
      } catch (e) {
        // Handle any JSON parsing errors
        localStorage.removeItem('trialWidgetCollapsed');
      }
    }
  }
};
</script>

<style scoped>
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.animate-shimmer {
  animation: shimmer 2s infinite;
}

.border-3 {
  border-width: 3px;
}

/* Override Vue Router active link styles */
a.router-link-active,
a.router-link-exact-active {
  background-color: transparent !important;
}
</style>
